<template>
  <section class="car-price-guide">
    <h3 class="car-price-guide__heading">Price guide</h3>

    <div class="car-price-guide__grid">
      <div class="car-price-guide__grid-item white-card border-bottom-thick yellow-border text-left">
        <h4 class="car-price-heading" v-text="formattedCarPrice" />
        <p class="car-price-desc">{{ priceCaption }}</p>
        <ExplanationModal
          button-text="Calculation explanation"
          with-icon
          :title="marketValueCalculationModalContent.title.value"
          :description="marketValueCalculationModalContent.description.value"
        />
      </div>

      <button @click="gotoCarLoanLink" class="car-price-guide__grid-item white-card border-bottom-thick navy-border with-icon text-left">
        <div class="d-flex space-between pt-2 items-flex-start">
          <img :src="CalculatorIcon" class="calculator-icon" alt="An icon of a piggy bank" />

          <div class="car-price-guide__grid-item content-card">
            <h4 class="white-card__title">Car loan calculator</h4>
            <p class="car-price-guide-text">Work out your weekly repayments</p>
          </div>
          <p class="top-3 relative button-as-link arrow-icon">&rarr;</p>
        </div>
      </button>

      <button @click="goToApplyForCarLoanLink" class="car-price-guide__grid-item white-card border-bottom-thick navy-border with-icon text-left">
        <div class="d-flex space-between pt-2 items-flex-start">
          <img :src="PiggyBankIcon" class="piggy-bank-icon" alt="An icon of a calculator" />
          <div class="car-price-guide__grid-item content-card">
            <h4 class="white-card__title">Apply for a car loan</h4>
            <p class="car-price-guide-text">Breeze through your next car purchase with an RACQ Bank Car Loan</p>
          </div>
          <p class="top-3 relative button-as-link arrow-icon" arrow-icon>&rarr;</p>
        </div>
      </button>
    </div>

    <hr class="car-price-guide-hr" />
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ExplanationModal from '../modals/ExplanationModal.vue';
import PiggyBankIcon from '@/assets/img/piggy-bank-dollar-coin.svg';
import CalculatorIcon from '@/assets/img/calculator.svg';
import { AnalyticsEventNames, AnalyticsEvents, AnalyticsEventTypes } from '../../foundation/enums/AnalyticsEvents';
import { useCarComparisonStore } from '../../foundation/stores/carComparisonStore';
import { useRoute } from 'vue-router';
import { useContentStore } from '../../foundation/stores/contentStore';

const props = defineProps({
  carPrice: {
    type: String,
    required: true,
    default: 'No price available',
  },
  isCurrent: {
    type: Boolean,
    default: false,
  },
});

const carComparisonStore = useCarComparisonStore();
const route = useRoute();
const contentStore = useContentStore();

contentStore.fetchModalDatasources();
const marketValueCalculationModalContent = contentStore.modalDatasources?.modalContentItems?.marketValueCalculation;

const formattedCarPrice = computed(() => {
  if (Number(props.carPrice) > 0) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AUD',
      currencyDisplay: 'narrowSymbol',
      currencySign: 'accounting',
      maximumFractionDigits: 0,
    }).format(Number(props.carPrice));
  }

  return 'N/A';
});

const priceCaption = computed(() =>
  props.carPrice === 'N/A' ? 'Pricing data not available' : props.isCurrent ? 'Estimated on-road price, QLD' : 'Indicative average used car price'
);

function pushToAdobe(link: string, isApplyLink: boolean) {
  try {
    carComparisonStore.pushToAnalytics(
      route.params.carId as string,
      AnalyticsEvents.NavigationLinkClick,
      isApplyLink ? AnalyticsEventNames.ApplyForACarLoan : AnalyticsEventNames.CarLoanCalculator,
      AnalyticsEventTypes.Promo,
      link
    );
  } catch (error) {
    console.log('fail to push to analytics', error);
  }
}

function gotoCarLoanLink() {
  const url = 'https://www.racq.com.au/car/car-loans/car-loan-calculator';
  pushToAdobe(url, false);
  window.location.href = url;
}

function goToApplyForCarLoanLink() {
  const url = 'https://www.racq.com.au/car/car-loans#products';
  pushToAdobe(url, true);
  window.location.href = url;
}
</script>

<style lang="scss" scoped>
.car-price-guide {
  padding-top: 0;
  margin-top: 3.75rem;

  &__heading {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }

  &__grid {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 1rem;

    @media screen and (min-width: #{$breakpoint-lg}) {
      flex-direction: row;
    }
  }

  &__grid-item {
    box-sizing: border-box;
    padding-left: 1.25rem;
    width: 100%;
    max-width: 27.5rem;

    @media screen and (min-width: #{$breakpoint-xl}) {
      max-width: 100%;
    }

    .content-card {
      display: flex;
      flex-direction: column;
      justify-items: start;
      align-items: start;
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      width: 70%;

      @media screen and (min-width: #{$breakpoint-md}) {
        width: 75%;
      }
    }

    &.with-icon {
      padding-left: 1rem;
      padding-right: 1.5rem;
    }

    .white-card__title {
      font-size: 1.25rem;
      line-height: 1.25;
      margin: 0;
      color: $racq-navy;
    }

    .car-price-heading {
      margin: 0.75rem 0 0.5rem;
      font-weight: bold;
      color: $racq-navy;
      font-size: 1.75rem;
    }

    .car-price-guide-text {
      height: 2rem;
      text-align: left;
      margin-top: 0.5rem;
      line-height: 1.125;
    }

    .car-price-desc {
      margin: 0 0 0.5rem;
    }
  }
}

.car-price-guide-hr {
  border: 1px solid $racq-pale-blue;
  margin: 4rem 0 0;
}

.calculator-icon,
.piggy-bank-icon {
  width: 50px;
  position: relative;
  top: 0.125rem;
}
</style>
